import { useEffect, useMemo, useState, type FormEvent } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { DEFAULT_BASE_URL, useAuth } from "../auth/AuthProvider";
import { getPublicBillingPlans } from "../api/client";
import { LangToggle, useI18n } from "../i18n";
import { formatAuthError } from "../lib/authErrors";
import { formatBillingError } from "../lib/billingErrors";
import { confirmStripeCheckout } from "../lib/stripeCheckout";
import type { BillingPlan, BillingPlansResponse, PublicBillingPlansResponse } from "../types/api";

const stripeCache = new Map<string, Promise<Stripe | null>>();
const zhPlanLabels = ["体验", "日常", "专业"];

function stripeFor(publishableKey: string): Promise<Stripe | null> {
  let p = stripeCache.get(publishableKey);
  if (!p) {
    p = loadStripe(publishableKey);
    stripeCache.set(publishableKey, p);
  }
  return p;
}

export function PurchaseScreen() {
  const { user, client, requestPurchaseEmailCode, verifyPurchaseEmailCode } = useAuth();
  const { t, lang } = useI18n();
  const [email, setEmail] = useState(user?.email ?? "");
  const [emailCode, setEmailCode] = useState("");
  const [password, setPassword] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [publicInfo, setPublicInfo] = useState<PublicBillingPlansResponse | null>(null);
  const [info, setInfo] = useState<BillingPlansResponse | null>(null);
  const [selected, setSelected] = useState<BillingPlan | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user?.email]);

  useEffect(() => {
    getPublicBillingPlans(DEFAULT_BASE_URL)
      .then(setPublicInfo)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!client) return;
    setError(null);
    client
      .getBillingPlans()
      .then(setInfo)
      .catch((e) => setError(formatBillingError(e, t, t("billing.loadFailed"))));
  }, [client, t]);

  const stripePromise = useMemo(
    () => (info?.publishableKey ? stripeFor(info.publishableKey) : null),
    [info?.publishableKey]
  );

  const planInfo = info ?? publicInfo;

  const fmt = (n: number) => {
    const currency = (planInfo?.currency || "cny").toUpperCase();
    if (currency === "CNY" || currency === "CNH" || currency === "RMB") return `¥${n.toFixed(2)}`;
    if (currency === "USD") return `$${n.toFixed(2)}`;
    return `${currency} ${n.toFixed(2)}`;
  };

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setCodeSent(false);
    setNeedsPassword(false);
    try {
      await requestPurchaseEmailCode(email.trim());
      setCodeSent(true);
    } catch (err) {
      setError(formatAuthError(err, t, t("auth.sendVerificationCodeFailed")));
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await verifyPurchaseEmailCode(email.trim(), emailCode.trim());
      if (result.needsPassword) {
        setNeedsPassword(true);
        return;
      }
    } catch (err) {
      setError(formatAuthError(err, t, t("auth.invalidVerificationCode")));
    } finally {
      setBusy(false);
    }
  }

  async function createAccount(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyPurchaseEmailCode(email.trim(), emailCode.trim(), password);
      setNeedsPassword(false);
    } catch (err) {
      setError(formatAuthError(err, t, t("register.failed")));
    } finally {
      setBusy(false);
    }
  }

  async function choosePlan(plan: BillingPlan) {
    if (!client) return;
    setError(null);
    setSelected(plan);
    try {
      const { clientSecret: cs } = await client.createPaymentIntent(plan.id);
      if (!cs) throw new Error("missing payment client secret");
      setClientSecret(cs);
    } catch (e) {
      setSelected(null);
      setError(formatBillingError(e, t, t("billing.failed")));
    }
  }

  async function onPaid() {
    setDone(true);
    for (let i = 0; i < 5 && client; i += 1) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        setInfo(await client.getBillingPlans());
      } catch {
        /* ignore */
      }
    }
  }

  const canVerify = Boolean(email.trim() && emailCode.trim());
  const canCreate = canVerify && password.length >= 8;

  return (
    <>
      <LangToggle className="floating" />
      <div className="purchase-page">
        <div className="purchase-shell">
          <section className="purchase-hero">
            <span className="title-logo">-/</span>
            <h1>{t("purchase.title")}</h1>
            <p>{t("purchase.subtitle")}</p>
          </section>

          <section className="purchase-panel panel">
            {error ? <div className="error-banner">{error}</div> : null}

            {!client ? (
              <>
                {publicInfo ? (
                  <section className="purchase-prices">
                    <h2 className="section-title">{t("purchase.priceList")}</h2>
                    <p className="muted">{t("purchase.priceListHint")}</p>
                    {publicInfo.enabled ? (
                      <div className="plan-list">
                        {publicInfo.plans.map((plan, idx) => (
                          <div key={plan.id} className="plan-card plan-card-static">
                            <span className="plan-name">{lang === "zh" ? (zhPlanLabels[idx] ?? plan.label) : plan.label}</span>
                            <span className="plan-price">{fmt(plan.priceUsd)}</span>
                            <span className="plan-quota muted">{t("purchase.planUsage", { quota: fmt(plan.quotaUsd) })}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="muted">{t("billing.notConfigured")}</p>
                    )}
                  </section>
                ) : null}

                <form onSubmit={sendCode}>
                  <h2 className="section-title">{t("purchase.verifyEmail")}</h2>
                  <label className="label" htmlFor="purchase-email">
                    {t("login.email")}
                  </label>
                  <input
                    id="purchase-email"
                    className="input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    disabled={busy || needsPassword}
                  />
                  <button type="submit" className="btn-secondary auth-code-button" disabled={busy || !email.trim() || needsPassword}>
                    {busy && !codeSent ? <span className="spinner inline" /> : t("purchase.sendCode")}
                  </button>
                  {codeSent ? <p className="muted-inline auth-code-status">{t("purchase.codeSent")}</p> : null}
                </form>

                {codeSent && !needsPassword ? (
                  <form onSubmit={verifyCode}>
                    <label className="label" htmlFor="purchase-code">
                      {t("login.emailCode")}
                    </label>
                    <input
                      id="purchase-code"
                      className="input"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value)}
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      placeholder={t("login.codePlaceholder")}
                      required
                      disabled={busy}
                    />
                    <button type="submit" className="btn-primary" disabled={busy || !canVerify}>
                      {busy ? <span className="spinner inline" /> : t("purchase.continue")}
                    </button>
                  </form>
                ) : null}

                {needsPassword ? (
                  <form onSubmit={createAccount}>
                    <h2 className="section-title">{t("purchase.setupPassword")}</h2>
                    <p className="muted">{t("purchase.newAccountHint")}</p>
                    <label className="label" htmlFor="purchase-password">
                      {t("register.password")}
                    </label>
                    <input
                      id="purchase-password"
                      className="input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      minLength={8}
                      required
                      disabled={busy}
                    />
                    <button type="submit" className="btn-primary" disabled={busy || !canCreate}>
                      {busy ? <span className="spinner inline" /> : t("purchase.createAndContinue")}
                    </button>
                  </form>
                ) : null}
              </>
            ) : info && info.enabled ? (
              <>
                <h2 className="section-title">{t("purchase.choosePlan")}</h2>
                {done ? <div className="ok-banner">{t("billing.success")}</div> : null}
                {!selected || !clientSecret ? (
                  <div className="plan-list">
                    {info.plans.map((plan, idx) => (
                      <button
                        key={plan.id}
                        type="button"
                        className="plan-card"
                        onClick={() => void choosePlan(plan)}
                        disabled={Boolean(selected)}
                      >
                        <span className="plan-name">{lang === "zh" ? (zhPlanLabels[idx] ?? plan.label) : plan.label}</span>
                        <span className="plan-price">{fmt(plan.priceUsd)}</span>
                        <span className="plan-quota muted">{t("purchase.planUsage", { quota: fmt(plan.quotaUsd) })}</span>
                      </button>
                    ))}
                  </div>
                ) : stripePromise ? (
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <PurchaseCheckoutForm
                      payLabel={t("billing.payNow", { price: fmt(selected.priceUsd) })}
                      backLabel={t("billing.back")}
                      onPaid={() => void onPaid()}
                      onBack={() => {
                        setSelected(null);
                        setClientSecret(null);
                      }}
                      onError={setError}
                    />
                  </Elements>
                ) : null}
              </>
            ) : info && !info.enabled ? (
              <p className="muted">{t("billing.notConfigured")}</p>
            ) : (
              <div className="center purchase-loading">
                <div className="spinner" />
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

function PurchaseCheckoutForm({
  payLabel,
  backLabel,
  onPaid,
  onBack,
  onError
}: {
  payLabel: string;
  backLabel: string;
  onPaid: () => void;
  onBack: () => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);

  async function pay() {
    if (!stripe || !elements) return;
    setBusy(true);
    onError("");
    try {
      await confirmStripeCheckout({
        stripe,
        elements,
        returnUrl: window.location.href,
        onPaid,
        onError,
        t
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="checkout-form">
      <PaymentElement />
      <button type="button" className="btn-primary settings-save" onClick={() => void pay()} disabled={busy || !stripe}>
        {busy ? <span className="spinner inline" /> : payLabel}
      </button>
      <button type="button" className="link-btn" onClick={onBack} disabled={busy}>
        {backLabel}
      </button>
    </div>
  );
}
